/**
 * MagicBI Design System - Typography Tokens
 * 
 * Complete typography system with semantic naming,
 * responsive scaling, and accessibility considerations.
 */

// Font families
export const fontFamilies = {
  sans: [
    '"Plus Jakarta Sans"',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    '"Fira Sans"',
    '"Droid Sans"',
    '"Helvetica Neue"',
    'sans-serif',
  ].join(', '),
  mono: [
    'SFMono-Regular',
    'Menlo',
    'Monaco',
    'Consolas',
    '"Liberation Mono"',
    '"Courier New"',
    'monospace',
  ].join(', '),
} as const;

// Font weights
export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

// Base font sizes (matching Plus Jakarta Sans spec)
export const fontSizes = {
  // Caption sizes (12px)
  'caption-4': '0.75rem',   // 12px - Caption 4 Regular
  'caption-3': '0.75rem',   // 12px - Caption 3 Medium  
  'caption-2': '0.75rem',   // 12px - Caption 2 Semibold
  'caption-1': '0.75rem',   // 12px - Caption 1 Bold
  
  // Overline sizes (14px)
  'overline-4': '0.625rem', // 10px - Overline 4 Regular (Figma design system)
  'overline-3': '0.875rem', // 14px - Overline 3 Medium
  'overline-2': '0.875rem', // 14px - Overline 2 Semibold
  'overline-1': '0.875rem', // 14px - Overline 1 Bold
  
  // Body sizes (14px)
  'body-4': '0.875rem',     // 14px - Body 4 Regular
  'body-3': '0.875rem',     // 14px - Body 3 Medium
  'body-2': '0.875rem',     // 14px - Body 2 Semibold
  'body-1': '0.875rem',     // 14px - Body 1 Bold
  
  // Subtitle sizes (16px)
  'subtitle-4': '1rem',     // 16px - Subtitle 4 Regular
  'subtitle-3': '1rem',     // 16px - Subtitle 3 Medium
  'subtitle-2': '1rem',     // 16px - Subtitle 2 Semibold
  'subtitle-1': '1rem',     // 16px - Subtitle 1 Bold
  
  // Heading sizes
  'h5': '1.125rem',         // 18px - H5 Medium
  'h4': '1.125rem',         // 18px - H4 Semibold
  'h3': '1.25rem',          // 20px - H3 Medium
  'h2': '1.25rem',          // 20px - H2 Semibold
  'h1': '1.875rem',         // 30px - H1 Semibold
  
  // Exact sizes (story redesign), keyed by px value
  '11': '0.6875rem', // 11px
  '13': '0.8125rem', // 13px
  '15': '0.9375rem', // 15px
  '17': '1.0625rem', // 17px
  '21': '1.3125rem',    // 21px (13px text)
  '22': '1.375rem',  // 22px
  '26': '1.625rem',  // 26px
  '28': '1.75rem',   // 28px

  // Legacy support
  xs: '0.75rem',     // 12px
  sm: '0.875rem',    // 14px
  base: '1rem',      // 16px
  lg: '1.125rem',    // 18px
  xl: '1.25rem',     // 20px
  '2xl': '1.5rem',   // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem',  // 36px
  '5xl': '3rem',     // 48px
  '6xl': '3.75rem',  // 60px
} as const;

// Line heights (matching Plus Jakarta Sans spec)
export const lineHeights = {
  // Specific line heights from spec
  '12': '0.75rem',     // 12px for Caption
  '14': '0.875rem',    // 14px for Overline (Figma design system)
  '16': '1rem',        // 16px for Body
  '18': '1.125rem',    // 18px for Subtitle
  '20': '1.25rem',     // 20px (13px / 15px text)
  '22': '1.375rem',    // 22px (15px text)
  '24': '1.5rem',      // 24px for Body/Subtitle
  '28': '1.75rem',     // 28px for H4/H5
  '30': '1.875rem',    // 30px for H2/H3
  '32': '2rem',        // 32px (26px text)
  '34': '2.125rem',    // 34px (26px / 28px text)
  '38': '2.375rem',    // 38px for H1
  
  // Standard ratios
  none: '1',
  tight: '1.25',
  snug: '1.375',
  normal: '1.5',
  relaxed: '1.625',
  loose: '2',
} as const;

// Letter spacing
export const letterSpacing = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
  /** Small-caps group label (11px overline) */
  overline: '0.08em',
  /** Small-caps kicker above a title */
  kicker: '0.14em',
} as const;

// Typography scale (Plus Jakarta Sans specification)
export const typography = {
  // Headings
  heading: {
    h1: {
      fontSize: fontSizes.h1,
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['38'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    h2: {
      fontSize: fontSizes.h2,
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['30'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    h3: {
      fontSize: fontSizes.h3,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['30'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    h4: {
      fontSize: fontSizes.h4,
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['28'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    h5: {
      fontSize: fontSizes.h5,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['28'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
  },

  // Subtitles
  subtitle: {
    1: {
      fontSize: fontSizes['subtitle-1'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    2: {
      fontSize: fontSizes['subtitle-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    3: {
      fontSize: fontSizes['subtitle-3'],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    4: {
      fontSize: fontSizes['subtitle-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
  },

  // Body text
  body: {
    1: {
      fontSize: fontSizes['body-1'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    2: {
      fontSize: fontSizes['body-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    3: {
      fontSize: fontSizes['body-3'],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    4: {
      fontSize: fontSizes['body-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    
    // Legacy support
    large: {
      fontSize: fontSizes['body-1'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    medium: {
      fontSize: fontSizes['body-3'],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    small: {
      fontSize: fontSizes['body-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
  },

  // Caption text
  caption: {
    1: {
      fontSize: fontSizes['caption-1'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    2: {
      fontSize: fontSizes['caption-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    3: {
      fontSize: fontSizes['caption-3'],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    4: {
      fontSize: fontSizes['caption-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    
    // Legacy support
    medium: {
      fontSize: fontSizes['caption-3'],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    small: {
      fontSize: fontSizes['caption-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
  },

  // Overline text
  overline: {
    1: {
      fontSize: fontSizes['overline-1'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights['16'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    2: {
      fontSize: fontSizes['overline-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['16'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    3: {
      fontSize: fontSizes['overline-3'],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['16'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    4: {
      fontSize: fontSizes['overline-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['14'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
  },

  // Labels and UI text (legacy support)
  label: {
    large: {
      fontSize: fontSizes['subtitle-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    medium: {
      fontSize: fontSizes['caption-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    small: {
      fontSize: fontSizes['caption-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
  },

  // Display text (legacy support)
  display: {
    large: {
      fontSize: fontSizes.h1,
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['38'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    medium: {
      fontSize: fontSizes.h2,
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights['30'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    small: {
      fontSize: fontSizes.h3,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights['30'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
  },

  // Code text
  code: {
    large: {
      fontSize: fontSizes['subtitle-1'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.mono,
    },
    medium: {
      fontSize: fontSizes['body-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['24'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.mono,
    },
    small: {
      fontSize: fontSizes['caption-4'],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights['18'],
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.mono,
    },
  },

  // Button text
  button: {
    large: {
      fontSize: fontSizes['subtitle-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights.none,
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    medium: {
      fontSize: fontSizes['body-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights.none,
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
    small: {
      fontSize: fontSizes['caption-2'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights.none,
      letterSpacing: letterSpacing.normal,
      fontFamily: fontFamilies.sans,
    },
  },
} as const;

// Responsive typography utilities
export const responsiveTypography = {
  // Scales down on mobile
  display: {
    large: {
      mobile: typography.display.medium,
      tablet: typography.display.large,
      desktop: typography.display.large,
    },
    medium: {
      mobile: typography.display.small,
      tablet: typography.display.medium,
      desktop: typography.display.medium,
    },
    small: {
      mobile: typography.heading.h1,
      tablet: typography.display.small,
      desktop: typography.display.small,
    },
  },
  heading: {
    h1: {
      mobile: typography.heading.h2,
      tablet: typography.heading.h1,
      desktop: typography.heading.h1,
    },
    h2: {
      mobile: typography.heading.h3,
      tablet: typography.heading.h2,
      desktop: typography.heading.h2,
    },
    h3: {
      mobile: typography.heading.h4,
      tablet: typography.heading.h3,
      desktop: typography.heading.h3,
    },
  },
} as const;

// Type definitions
export type TypographyTokens = typeof typography;
export type TypographyCategory = keyof TypographyTokens;
export type TypographyVariant<T extends TypographyCategory> = keyof TypographyTokens[T];

// Utility functions
export const getTypographyStyle = <T extends TypographyCategory>(
  category: T,
  variant: TypographyVariant<T>
) => {
  return typography[category][variant];
};

// CSS helper for styled-components
export const typographyMixin = <T extends TypographyCategory>(
  category: T,
  variant: TypographyVariant<T>
) => {
  const style = (typography[category] as any)[variant];
  return `
    font-family: ${style?.fontFamily || fontFamilies.sans};
    font-size: ${style?.fontSize || fontSizes.base};
    font-weight: ${style?.fontWeight || fontWeights.regular};
    line-height: ${style?.lineHeight || lineHeights.normal};
    letter-spacing: ${style?.letterSpacing || letterSpacing.normal};
  `;
};
